import { startTransition, useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { convertValuesToStrings, transformRegulatorDataForForm } from '../utils/formDataTransformers'
// import { RPM_FIELD_LETTERS } from '../constants/rpmFieldConstants' // Currently unused

// API constants
const API_ENDPOINTS = {
  CONFIG: '/config',
}

const API_HEADERS = {
  'Content-Type': 'application/json',
}

// Field configuration constants - EXPORTED for shared use
export const ALTERNATOR_FIELDS = [
  'fieldVoltage', 'fieldPolarity', 'targetTemp', 'startupDelay', 'statorInstalled',
  'altShuntInstalled', 'altShuntAmps', 'altShuntMv', 'boostMode', 'rpmReduceField', 'maxRpm',
  'pulleyRatio', 'altPoles', 'minField', 'chargerInstance', 'engineInstance', 'engineLoadThreshold', 'altCurrentLimit',
]

export const BATTERY_FIELDS = [
  'voltConnected', 'tempConnected', 'maxChargingTemp', 'minChargingTemp', 'tempSlope',
  'shuntInstalled', 'shuntAmps', 'shuntMv', 'ampHrCapacity', 'cRate', 'cvVolts', 'cvMinTime', 'cvMaxTime',
  'cvFieldPct', 'cvMinAmps', 'postCvSwitch', 'postCvVolts', 'postCvMinTime', 'postCvMaxTime', 'postCvFieldPct',
  'postCvMinAmps', 'floatVolts', 'floatMinTime',
  'floatMaxTime', 'floatMinVolts', 'floatFieldPctMax', 'floatMaxAmpHrs', 'chargeCutoffInstalled',
  'chargeCutoffVoltage', 'chargeCutoffPolarity', 'numBatsInBank', 'canbusRequired',
]

// Helper to generate dual field configurations - EXPORTED for shared use
export const createDualFields = (fields) => Object.fromEntries(
  fields.flatMap(field => [[field, ''], [`${field}2`, '']]),
)

// Generate instance fields for batteries (A-J) and RPM charts (A-I) - EXPORTED for shared use
export const createInstanceFields = (prefix, letters, suffix = '') => Object.fromEntries(
  letters.split('').flatMap(letter => [
    [`${prefix}${letter}${suffix}`, ''],
    [`${prefix}${letter}${suffix}2`, ''],
  ]),
)

// Comprehensive default configuration - EXPORTED for shared use
export const DEFAULT_CONFIG = {
  // General Configuration
  numAlternatorsInstalled: 'numAlternatorsInstalled1',
  numBatteriesManaged: 'numBatteriesManaged1',
  networkType: 'ethernet',
  networkName: '',
  regulatorPassword: '',
  configPassword: '',
  canbusType: '',
  canbusRvc: false,
  canbusMarine: false,
  autoUpdateSwitch: false,

  // BMS Protocol Configuration
  enableBmsProtocols: false,
  bmsProtocol: 'none',
  bmsBitrate: '250000',

  // Battery dropdown selections (for Battery Bank 1)
  batteryVoltage: '12',
  batteryQuickType: '',
  batteryQuickBrand: '',
  batteryQuickModel: '',

  // Battery dropdown selections (for Battery Bank 2)
  batteryVoltage2: '12',
  batteryQuickType2: '',
  batteryQuickBrand2: '',
  batteryQuickModel2: '',

  // Generated dual fields
  ...createDualFields(ALTERNATOR_FIELDS),
  ...createDualFields(BATTERY_FIELDS),
  ...createInstanceFields('batteryInstance', 'ABCDEFGHIJ'),
  // RPM fields are generated dynamically by RpmFieldChart component
}

// Data processing utilities - EXPORTED for shared use
export const detectAlternatorConfiguration = (transformedData) => {
  // Only set to 'numBatteriesManaged2' if the config file explicitly has numBatteriesManaged2 field
  const hasBattery2 = transformedData.numBatteriesManaged2 === 'numBatteriesManaged2'

  return {
    // Reconstruct UI state: explicit default to single battery unless explicitly set to two
    numBatteriesManaged: hasBattery2 ? 'numBatteriesManaged2' : 'numBatteriesManaged1',
  }
}

export const processConfigurationData = (configData) => {
  const transformedData = transformRegulatorDataForForm(configData)

  // IMPORTANT: Always run detection to properly reconstruct UI state
  // This is critical for fields like numBatteriesManaged which are excluded from exports
  const config = detectAlternatorConfiguration(transformedData)

  // Set alternator count to default if not explicitly set (user controls this setting)
  if (!transformedData.numAlternatorsInstalled) {
    transformedData.numAlternatorsInstalled = 'numAlternatorsInstalled1'
  }

  // CRITICAL: Always use detected battery management state
  // This reconstructs the UI state from the actual config data
  transformedData.numBatteriesManaged = config.numBatteriesManaged

  // Fallback to default only if detection failed completely
  if (!transformedData.numBatteriesManaged) {
    transformedData.numBatteriesManaged = 'numBatteriesManaged1'
  }

  return transformedData
}

// Optimized shallow comparison for dirty checking - EXPORTED for shared use
export const hasChanges = (current, saved) => {
  if (!saved) return false

  // Check if any current values differ from saved values
  for (const key in current) {
    if (current[key] !== saved[key]) return true
  }

  // Check if saved has any keys not in current
  for (const key in saved) {
    if (!(key in current)) return true
  }

  return false
}

// Data preprocessing for save operations - EXPORTED for shared use
export const preprocessConfigDataForSave = (data) => {
  // Use shared utility to convert all values to strings consistently
  // Includes special handling for batteryVoltage fields to preserve their values
  return convertValuesToStrings(data)
}

const useConfigurationData = () => {
  const [isDirty, setIsDirty] = useState(false)
  const [lastSavedData, setLastSavedData] = useState(null)
  const [formData, setFormData] = useState(DEFAULT_CONFIG)
  const queryClient = useQueryClient()

  // Fetch configuration data
  const { data: configData, isLoading, error } = useQuery({
    queryKey: ['configuration'],
    queryFn: async () => {
      const response = await fetch(API_ENDPOINTS.CONFIG)
      if (!response.ok) {
        throw new Error(`Failed to fetch configuration: ${response.status} ${response.statusText}`)
      }
      return response.json()
    },
  })

  // Memoize processed configuration data to avoid unnecessary recalculations
  const processedConfigData = useMemo(() => {
    if (!configData || Object.keys(configData).length === 0) return null
    return processConfigurationData(configData)
  }, [configData])

  // Save configuration mutation
  const saveConfigMutation = useMutation({
    mutationFn: async (data) => {
      const processedData = preprocessConfigDataForSave(data)

      const response = await fetch(API_ENDPOINTS.CONFIG, {
        method: 'POST',
        headers: API_HEADERS,
        body: JSON.stringify(processedData),
      })

      if (!response.ok) {
        throw new Error(`Failed to save configuration: ${response.status} ${response.statusText}`)
      }
      return response.json()
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(['configuration'])
      setLastSavedData(data)
      setIsDirty(false)
    },
  })

  // Update form data when config data is loaded
  useEffect(() => {
    if (!configData) return

    const hasConfigData = Object.keys(configData).length > 0

    if (hasConfigData && processedConfigData) {
      // Use React 19's startTransition for non-urgent updates
      startTransition(() => {
        setFormData(processedConfigData)
        setLastSavedData(processedConfigData)
        setIsDirty(false)
      })
    } else {
      // First-time setup - batch state updates
      startTransition(() => {
        setFormData(DEFAULT_CONFIG)
        setLastSavedData(null)
        setIsDirty(false)
      })
    }
  }, [configData, processedConfigData])

  // Track form changes for dirty state  
  const updateFormData = useCallback((updates) => {
    // Use React 19's startTransition for non-urgent form updates
    startTransition(() => {
      setFormData(prevData => {
        const newData = { ...prevData, ...updates }
        return newData
      })

      // Update dirty state separately to avoid dependency issues
      setIsDirty(_prevIsDirty => {
        const newIsDirty = hasChanges({ ...formData, ...updates }, lastSavedData)
        return newIsDirty
      })
    })
  }, [formData, lastSavedData])

  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target
    updateFormData({ [name]: type === 'checkbox' ? checked : value })
  }, [updateFormData])

  const saveConfiguration = useCallback((dataToSave = formData) =>
    saveConfigMutation.mutate(dataToSave), [saveConfigMutation, formData])

  const resetForm = useCallback(() => {
    if (lastSavedData) {
      // Use React 19's startTransition for non-urgent reset
      startTransition(() => {
        setFormData(lastSavedData)
        setIsDirty(false)
      })
    }
  }, [lastSavedData])
  return {
    formData,
    setFormData,
    updateFormData,
    handleInputChange,
    saveConfiguration,
    resetForm,
    isLoading,
    error,
    isSaving: saveConfigMutation.isPending,
    saveError: saveConfigMutation.error,
    isDirty,
    lastSavedData,
  }
}

export default useConfigurationData
